/**
 * Shared formatting utilities for the dashboard and other pages.
 */

/**
 * Format a duration in seconds to a human-readable short string.
 * @example formatDuration(3720) // "1h 2m"
 * @example formatDuration(180) // "3 min"
 * @example formatDuration(45) // "0 min"
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes} min`;
}

/**
 * Format a duration in seconds to a timestamp string (MM:SS or HH:MM:SS).
 * Useful for progress displays and player UI.
 * @example formatDurationTimestamp(3720) // "1:02:00"
 * @example formatDurationTimestamp(180) // "3:00"
 * @example formatDurationTimestamp(65) // "1:05"
 */
export function formatDurationTimestamp(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format a date string to a localized short date.
 * @example formatDate("2025-01-15T08:00:00Z") // "Jan 15, 2025"
 */
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date string to a relative time (e.g., "2h ago").
 * Falls back to a short date format for dates older than 7 days.
 * @example formatRelativeDate(recentDate) // "5m ago", "2h ago", "3d ago"
 * @example formatRelativeDate(oldDate) // "Jan 15"
 */
export function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
